/**
 * User achievements (profile). Unlocks tie to roadmap tracks: initiated + completed.
 * Storage: users/{userId}/achievements/{achievementDocId}
 *
 * Call registerTrackPublishedAchievement from track publishing pipeline (admin) when ready.
 */

import { collection, doc, documentId, getDoc, getDocs, query, setDoc, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { computeTrackSnippetMastery } from '../components/Roadmap/RoadmapList';
import { normalizeRoadmapLanguageKey } from '../utils/roadmapDisplay';
import { loadRoadmapSnippetStatsByIds } from './profileAchievementStatsService';
import { loadTrackNodes, loadTrackProgress, loadPublishedTracks } from './roadmapService';

export const ACHIEVEMENT_TYPES = {
  TRACK_INITIATED: 'track_initiated',
  TRACK_COMPLETED: 'track_completed',
  TRACK_PUBLISHED: 'track_published'
};

/** Stable doc id for a per-track achievement */
export function achievementDocId(type, trackId) {
  const safe = String(trackId).replace(/[/\\]/g, '_');
  return `${type}__${safe}`;
}

export function normalizeLanguageKey(lang) {
  if (!lang || typeof lang !== 'string') return 'other';
  const trimmed = lang.trim();
  if (!trimmed) return 'other';
  const canonical = normalizeRoadmapLanguageKey(trimmed);
  return canonical || 'other';
}

export async function fetchTrackMeta(trackId) {
  try {
    const ref = doc(db, 'tracks', trackId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return { title: null, language: null };
    const d = snap.data();
    return {
      title: (d.title && String(d.title).trim()) || null,
      language: d.language || null
    };
  } catch {
    return { title: null, language: null };
  }
}

const TRACK_META_BATCH_IN_LIMIT = 30;

/**
 * Batch-read track titles/languages (replaces N parallel getDoc calls on profile Skills).
 * @param {string[]} trackIds
 * @returns {Promise<Map<string, { title: string | null, language: string | null }>>}
 */
export async function fetchTrackMetaBatch(trackIds) {
  /** @type {Map<string, { title: string | null, language: string | null }>} */
  const map = new Map();
  const unique = [...new Set((trackIds || []).filter((id) => typeof id === 'string' && id.trim()))];
  if (!unique.length) return map;

  const chunks = [];
  for (let i = 0; i < unique.length; i += TRACK_META_BATCH_IN_LIMIT) {
    chunks.push(unique.slice(i, i + TRACK_META_BATCH_IN_LIMIT));
  }

  await Promise.all(
    chunks.map(async (ids) => {
      if (!ids.length) return;
      try {
        const snap = await getDocs(
          query(collection(db, 'tracks'), where(documentId(), 'in', ids))
        );
        snap.forEach((d) => {
          const data = d.data();
          map.set(d.id, {
            title: (data.title && String(data.title).trim()) || null,
            language: data.language || null
          });
        });
      } catch (e) {
        console.warn('fetchTrackMetaBatch: batch read failed', e?.code, e?.message);
      }
    })
  );

  return map;
}

/**
 * Unlock "started a track" when the learner creates a roadmap cookbook from a node (first touch).
 * Idempotent.
 */
export async function maybeUnlockTrackStarted(userId, trackId) {
  if (!userId || !trackId) return;
  const id = achievementDocId(ACHIEVEMENT_TYPES.TRACK_INITIATED, trackId);
  const ref = doc(db, 'users', userId, 'achievements', id);
  const existing = await getDoc(ref);
  if (existing.exists()) return;

  const meta = await fetchTrackMeta(trackId);
  await setDoc(ref, {
    type: ACHIEVEMENT_TYPES.TRACK_INITIATED,
    trackId,
    trackTitle: meta.title || '',
    language: normalizeLanguageKey(meta.language),
    unlockedAt: serverTimestamp()
  });
}

/**
 * After any node completion, check if every testable node (has snippetIds) is done → track completed.
 * Idempotent.
 */
export async function maybeUnlockTrackCompleted(userId, trackId) {
  if (!userId || !trackId) return;
  const id = achievementDocId(ACHIEVEMENT_TYPES.TRACK_COMPLETED, trackId);
  const ref = doc(db, 'users', userId, 'achievements', id);
  const existing = await getDoc(ref);
  if (existing.exists()) return;

  const nodes = await loadTrackNodes(trackId);
  const requiredIds = nodes.filter((n) => Array.isArray(n.snippetIds) && n.snippetIds.length > 0).map((n) => n.id);
  if (requiredIds.length === 0) return;

  const { completedNodeIds } = await loadTrackProgress(userId, trackId);
  const setDone = new Set(completedNodeIds || []);
  const allDone = requiredIds.every((nid) => setDone.has(nid));
  if (!allDone) return;

  const meta = await fetchTrackMeta(trackId);
  await setDoc(ref, {
    type: ACHIEVEMENT_TYPES.TRACK_COMPLETED,
    trackId,
    trackTitle: meta.title || '',
    language: normalizeLanguageKey(meta.language),
    unlockedAt: serverTimestamp()
  });
}

/**
 * Call after markNodeCompleted (progress just updated).
 */
export async function maybeUnlockTrackAchievementAfterProgress(userId, trackId) {
  await maybeUnlockTrackStarted(userId, trackId);
  await maybeUnlockTrackCompleted(userId, trackId);
}

/**
 * When a track is published to Firestore (author / pipeline). Optional language + title from caller.
 */
export async function registerTrackPublishedAchievement(userId, trackId, options = {}) {
  if (!userId || !trackId) return;
  const id = achievementDocId(ACHIEVEMENT_TYPES.TRACK_PUBLISHED, trackId);
  const ref = doc(db, 'users', userId, 'achievements', id);
  const existing = await getDoc(ref);
  if (existing.exists()) return;

  const meta = options.title != null || options.language != null
    ? { title: options.title || '', language: options.language || null }
    : await fetchTrackMeta(trackId);

  await setDoc(ref, {
    type: ACHIEVEMENT_TYPES.TRACK_PUBLISHED,
    trackId,
    trackTitle: meta.title || options.title || '',
    language: normalizeLanguageKey(meta.language || options.language),
    unlockedAt: serverTimestamp()
  });
}

/**
 * Load all unlocked achievements for profile UI.
 */
export async function loadUserAchievements(userId) {
  if (!userId) return [];
  const col = collection(db, 'users', userId, 'achievements');
  const snap = await getDocs(col);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Tracks the user has started (roadmap cookbook and/or track_progress). One row per track with snippet mastery.
 * @param {string} userId
 * @param {{ decksSnap?: import('firebase/firestore').QuerySnapshot, trackProgressSnap?: import('firebase/firestore').QuerySnapshot }} [preloaded]
 */
export async function loadInitiatedTrackProgressCards(userId, preloaded = {}) {
  if (!userId) return [];

  const trackIds = new Set();

  const [decksSnap, progressSnap, publishedTracks] = await Promise.all([
    preloaded.decksSnap
      ? Promise.resolve(preloaded.decksSnap)
      : getDocs(collection(db, `users/${userId}/decks`)),
    preloaded.trackProgressSnap
      ? Promise.resolve(preloaded.trackProgressSnap)
      : getDocs(collection(db, `users/${userId}/track_progress`)),
    loadPublishedTracks().catch(() => [])
  ]);

  decksSnap.forEach((d) => {
    const data = d.data();
    if (data.roadmapTrackId && (data.type === 'cookbook' || !data.type)) {
      trackIds.add(data.roadmapTrackId);
    }
  });

  progressSnap.forEach((d) => trackIds.add(d.id));

  const ids = Array.from(trackIds);
  if (ids.length === 0) return [];

  const publishedIdSet = new Set(
    publishedTracks.map((t) => String(t.trackId || t.contentId || t.id || '').trim()).filter(Boolean)
  );
  const eligibleIds = ids.filter((trackId) => publishedIdSet.has(trackId));
  if (eligibleIds.length === 0) return [];

  const progressByTrackId = new Map();
  progressSnap.forEach((d) => {
    const data = d.data();
    progressByTrackId.set(d.id, {
      completedNodeIds: Array.isArray(data.completedNodeIds) ? data.completedNodeIds : []
    });
  });

  const [metaById, nodeEntries] = await Promise.all([
    fetchTrackMetaBatch(eligibleIds),
    Promise.all(eligibleIds.map(async (trackId) => [trackId, await loadTrackNodes(trackId)]))
  ]);
  const nodesByTrackId = new Map(nodeEntries);

  const snippetIdSet = new Set();
  nodeEntries.forEach(([, nodes]) => {
    (nodes || []).forEach((node) => {
      (node.snippetIds || []).forEach((id) => {
        if (id) snippetIdSet.add(id);
      });
    });
  });

  const snippetStatsById = await loadRoadmapSnippetStatsByIds(userId, Array.from(snippetIdSet));

  const cards = eligibleIds.map((trackId) => {
    const nodes = nodesByTrackId.get(trackId) || [];
    const meta = metaById.get(trackId) || { title: null, language: null };
    const progress = progressByTrackId.get(trackId) || { completedNodeIds: [] };

    const requiredIds = nodes
      .filter((n) => Array.isArray(n.snippetIds) && n.snippetIds.length > 0)
      .map((n) => n.id);
    const totalRequired = requiredIds.length;
    const completedSet = new Set(progress.completedNodeIds || []);
    const completedCount = requiredIds.filter((nid) => completedSet.has(nid)).length;

    const { total: snippetTotal, mastered: snippetMastered } = computeTrackSnippetMastery(
      nodes,
      snippetStatsById
    );

    const language = normalizeLanguageKey(meta.language);
    const trackTitle = (meta.title && String(meta.title).trim()) || trackId;

    const isComplete = snippetTotal > 0 && snippetMastered >= snippetTotal;

    return {
      trackId,
      trackTitle,
      language,
      totalRequired,
      completedCount,
      snippetTotal,
      snippetMastered,
      isComplete
    };
  });

  return cards.sort((a, b) =>
    a.trackTitle.localeCompare(b.trackTitle, undefined, { sensitivity: 'base' })
  );
}
